/**
 * Centralized Analytics Service for Interview Platform
 *
 * Abstracts Google Analytics away from components.
 * Uses ReactGA (react-ga) which is already installed in the project.
 * Can be swapped for Mixpanel, Amplitude, GA4, etc. later by changing this single file.
 *
 * Event Categories:
 * - LoginFlow: Invitation & login events (Stage 1)
 * - DeviceSetup: Camera, mic, screen share events (Stage 2)
 * - Lobby: Lobby & admission events (Stage 3)
 * - InterviewSession: Session lifecycle events (Stage 4)
 * - Communication: Mic, camera, chat engagement events
 * - CodeEditor: Code editor interaction events
 * - ProblemInteraction: Problem lifecycle events
 * - Stability: Network disconnection & reconnection events
 * - ExitRejoin: Leave, rejoin, abandonment events
 * - API: API failure tracking
 */
import ReactGA from 'react-ga4';

const analytics = {
  /**
   * Reads the current user & interview context from localStorage.
   * Returns an object that can be spread into GA custom dimensions.
   */
  _getUserContext() {
    return {
      interview_id: localStorage.getItem('interview_id') || undefined,
      role: localStorage.getItem('role') || undefined,
      email: localStorage.getItem('username') || undefined,
      company_id: localStorage.getItem('company_id') || undefined,
    };
  },

  /**
   * Call this after login / invite validation so GA associates
   * all subsequent hits with the user. Sets GA userId and
   * custom dimensions that persist for the page session.
   */
  setUserContext() {
    const email = localStorage.getItem('username');
    const role = localStorage.getItem('role');
    const companyId = localStorage.getItem('company_id');
    const interviewId = localStorage.getItem('interview_id');

    const dims = {};
    if (email) dims.user_id = email;
    if (role) dims.role = role;
    if (companyId) dims.company_id = companyId;
    if (interviewId) dims.interview_id = interviewId;

    if (Object.keys(dims).length) {
      ReactGA.set(dims);
    }
  },

  /**
   * Every event is automatically enriched with the current user context
   * (interview_id, role, email, company_id) from localStorage.
   * The context is appended to the label so it's visible in GA reports
   * even without custom dimensions being configured in the GA admin panel.
   */
  trackEvent(category, action, label, params = {}) {
    // Allow callers to pass a primitive value as the fourth arg for
    // convenience. Normalize to an object so downstream code always
    // receives key/value pairs (e.g. { value: 123 }).
    if (params !== undefined && params !== null && typeof params !== 'object') {
      params = { value: params };
    } else {
      params = params || {};
    }

    const ctx = this._getUserContext();

    // Avoid duplicating identifiers; prefer context values.
    const sanitizedParams = { ...params };
    ['interview_id', 'company_id'].forEach(key => {
      if (ctx[key] && sanitizedParams[key]) {
        delete sanitizedParams[key];
      }
    });

    const eventPayload = {
      event_category: category,
      ...(label && { event_label: label }),
      ...ctx,
      ...sanitizedParams,
    };

    ReactGA.event(action, eventPayload);
  },

  trackInterviewLinkClicked(interviewId) {
    this.trackEvent('LoginFlow', 'interview_link_clicked', undefined, {
      interview_id: interviewId,
    });
  },

  trackLoginMethodSelected(method) {
    this.trackEvent('LoginFlow', 'login_method_selected', undefined, { method });
  },

  trackOtpRequested() {
    this.trackEvent('LoginFlow', 'otp_requested');
  },

  trackOtpVerifiedSuccess() {
    this.trackEvent('LoginFlow', 'otp_verified_success');
  },

  trackOtpVerifiedFailure(failureReason) {
    this.trackEvent('LoginFlow', 'otp_verified_failure', undefined, {
      failure_reason: failureReason,
    });
  },

  trackGoogleLoginSuccess() {
    this.trackEvent('LoginFlow', 'google_login_success');
  },

  trackGoogleLoginFailure(errorMessage) {
    this.trackEvent('LoginFlow', 'google_login_failure', undefined, {
      error_message: errorMessage,
    });
  },

  trackCameraPermissionGranted() {
    this.trackEvent('DeviceSetup', 'camera_permission_granted');
  },

  trackCameraPermissionDenied() {
    this.trackEvent('DeviceSetup', 'camera_permission_denied');
  },

  trackMicrophonePermissionGranted() {
    this.trackEvent('DeviceSetup', 'microphone_permission_granted');
  },

  trackMicrophonePermissionDenied() {
    this.trackEvent('DeviceSetup', 'microphone_permission_denied');
  },

  trackScreenShareStarted() {
    this.trackEvent('DeviceSetup', 'screen_share_started');
  },

  trackScreenShareFailed(errorMessage) {
    this.trackEvent('DeviceSetup', 'screen_share_failed', undefined, {
      error_message: errorMessage,
    });
  },

  trackDeviceSetupCompleted() {
    this.trackEvent('DeviceSetup', 'device_setup_completed');
  },

  trackEnteredLobby(interviewId) {
    this.trackEvent('Lobby', 'entered_lobby', undefined, { interview_id: interviewId });
  },

  trackAdmittedToInterview(interviewId) {
    this.trackEvent('Lobby', 'admitted_to_interview', undefined, { interview_id: interviewId });
  },

  trackInterviewSessionStarted(interviewId) {
    this.trackEvent('InterviewSession', 'interview_session_started', undefined, {
      interview_id: interviewId,
    });
  },

  trackInterviewSessionEnded(interviewId, duration, exitType) {
    this.trackEvent('InterviewSession', 'interview_session_ended', undefined, {
      interview_id: interviewId,
      exit_type: exitType,
      duration,
    });
  },

  trackMicToggled(state) {
    this.trackEvent('Communication', 'mic_toggled', undefined, { state });
  },

  trackCameraToggled(state) {
    this.trackEvent('Communication', 'camera_toggled', undefined, { state });
  },

  trackChatMessageSent(messageLength) {
    this.trackEvent('Communication', 'chat_message_sent', undefined, {
      message_length: messageLength,
    });
  },

  trackScreenShareStopped() {
    this.trackEvent('Communication', 'screen_share_stopped');
  },

  trackCodeEditorLoaded(interviewId) {
    this.trackEvent('CodeEditor', 'code_editor_loaded', undefined, { interview_id: interviewId });
  },

  trackCodeWritten(linesAdded, charactersAdded) {
    this.trackEvent('CodeEditor', 'code_written', undefined, {
      lines_added: linesAdded,
      characters_added: charactersAdded,
    });
  },

  trackCodeRunClicked(language, problemId) {
    this.trackEvent('CodeEditor', 'code_run_clicked', undefined, {
      language,
      problem_id: problemId,
    });
  },

  trackCodeExecutionResult(status, language) {
    this.trackEvent('CodeEditor', 'code_execution_result', undefined, { status, language });
  },

  trackLanguageChanged(language, problemId) {
    this.trackEvent('CodeEditor', 'language_changed', undefined, {
      language,
      ...(problemId && { problem_id: problemId }),
    });
  },

  trackCustomInputUsed() {
    this.trackEvent('CodeEditor', 'custom_input_used');
  },

  trackThemeChanged(theme) {
    this.trackEvent('CodeEditor', 'theme_changed', undefined, { theme });
  },

  trackFullscreenEnabled() {
    this.trackEvent('CodeEditor', 'fullscreen_enabled');
  },

  trackFullscreenDisabled() {
    this.trackEvent('CodeEditor', 'fullscreen_disabled');
  },

  trackProblemAssigned(problemId, interviewId) {
    this.trackEvent('ProblemInteraction', 'problem_assigned', undefined, {
      problem_id: problemId,
      interview_id: interviewId,
    });
  },

  trackProblemOpened(problemId) {
    this.trackEvent('ProblemInteraction', 'problem_opened', undefined, { problem_id: problemId });
  },

  trackProblemSubmitted(problemId, finalStatus, totalRunAttempts) {
    this.trackEvent('ProblemInteraction', 'problem_submitted', undefined, {
      problem_id: problemId,
      final_status: finalStatus,
      total_run_attempts: totalRunAttempts,
    });
  },

  trackResultsViewed(problemId) {
    this.trackEvent('ProblemInteraction', 'results_viewed', undefined, { problem_id: problemId });
  },

  trackNetworkDisconnected() {
    this.trackEvent('Stability', 'network_disconnected');
  },

  trackWsConnected(wsType) {
    this.trackEvent('Stability', 'ws_connected', undefined, { ws_type: wsType });
  },

  trackWsDisconnected(wsType, code, reason) {
    this.trackEvent('Stability', 'ws_disconnected', undefined, {
      ws_type: wsType,
      code,
      reason: reason || 'no_reason',
    });
  },

  trackWsError(wsType) {
    this.trackEvent('Stability', 'ws_error', undefined, { ws_type: wsType });
  },

  trackWsReconnectAttempted(wsType, attemptNumber) {
    this.trackEvent('Stability', 'ws_reconnect_attempted', undefined, {
      ws_type: wsType,
      attemptNumber,
    });
  },

  trackAutoReconnectAttempted() {
    this.trackEvent('Stability', 'auto_reconnect_attempted');
  },

  trackReconnectSuccess() {
    this.trackEvent('Stability', 'reconnect_success');
  },

  trackReconnectFailed() {
    this.trackEvent('Stability', 'reconnect_failed');
  },

  trackManualReconnectClicked() {
    this.trackEvent('Stability', 'manual_reconnect_clicked');
  },

  trackLeaveMeetingClicked(interviewId) {
    this.trackEvent('ExitRejoin', 'leave_meeting_clicked', undefined, {
      interview_id: interviewId,
    });
  },

  trackInterviewRejoined(interviewId) {
    this.trackEvent('ExitRejoin', 'interview_rejoined', undefined, { interview_id: interviewId });
  },

  trackInterviewAbandoned(interviewId) {
    this.trackEvent('ExitRejoin', 'interview_abandoned', undefined, { interview_id: interviewId });
  },

  trackApiFailure(endpoint, status, method, errorMessage) {
    this.trackEvent('API', 'api_failure', undefined, {
      endpoint,
      status,
      method,
      ...(errorMessage && { error_message: errorMessage }),
    });
  },

  trackInviteLinkOpened(interviewId, role) {
    this.trackEvent('LoginFlow', 'invite_link_opened', undefined, {
      interview_id: interviewId,
      role,
    });
  },

  trackLoginCompleted(method, interviewId) {
    this.trackEvent('LoginFlow', 'login_completed', undefined, {
      method,
      interview_id: interviewId,
    });
  },

  trackMediaPermissionsGranted(interviewId, permissions) {
    this.trackEvent('DeviceSetup', 'media_permissions_granted', undefined, {
      interview_id: interviewId,
      ...permissions,
    });
  },

  trackLobbyEntered(interviewId) {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('lobby_entered_ts', Date.now().toString());
    }
    this.trackEvent('Lobby', 'lobby_entered', undefined, { interview_id: interviewId });
  },

  trackInterviewStarted(interviewId, recordingEnabled) {
    const lobbyTs =
      typeof sessionStorage !== 'undefined'
        ? parseInt(sessionStorage.getItem('lobby_entered_ts') || '0', 10)
        : 0;
    const waitTimeSec = lobbyTs ? Math.round((Date.now() - lobbyTs) / 1000) : undefined;
    this.trackEvent('InterviewSession', 'interview_started', undefined, {
      interview_id: interviewId,
      recording_enabled: recordingEnabled,
      ...(waitTimeSec !== undefined && { candidate_wait_time_sec: waitTimeSec }),
      timestamp: Date.now(),
    });
  },

  trackInterviewEnded(interviewId, recordingEnabled) {
    this.trackEvent('InterviewSession', 'interview_ended', undefined, {
      interview_id: interviewId,
      recording_enabled: recordingEnabled,
      timestamp: Date.now(),
    });
  },

  trackFeedbackPageOpened(interviewId) {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('feedback_page_opened_ts', Date.now().toString());
    }
    this.trackEvent('Feedback', 'feedback_page_opened', undefined, {
      interview_id: interviewId,
      timestamp: Date.now(),
    });
  },

  trackFeedbackSubmitted(interviewId, rating) {
    const openedTs =
      typeof sessionStorage !== 'undefined'
        ? parseInt(sessionStorage.getItem('feedback_page_opened_ts') || '0', 10)
        : 0;
    const timeToSubmitSec = openedTs ? Math.round((Date.now() - openedTs) / 1000) : undefined;
    this.trackEvent('Feedback', 'feedback_submitted', undefined, {
      interview_id: interviewId,
      rating,
      ...(timeToSubmitSec !== undefined && { time_to_submit_sec: timeToSubmitSec }),
      timestamp: Date.now(),
    });
  },

  trackSessionDisconnected(interviewId, reason) {
    this.trackEvent('Stability', 'session_disconnected', undefined, {
      interview_id: interviewId,
      reason,
      timestamp: Date.now(),
    });
  },

  trackSessionReconnected(interviewId, method) {
    this.trackEvent('Stability', 'session_reconnected', undefined, {
      interview_id: interviewId,
      method,
      timestamp: Date.now(),
    });
  },

  trackAudioFailureDetected(interviewId, errorMessage) {
    this.trackEvent('Stability', 'audio_failure_detected', undefined, {
      interview_id: interviewId,
      error_message: errorMessage,
    });
  },

  trackVideoFailureDetected(interviewId, errorMessage) {
    this.trackEvent('Stability', 'video_failure_detected', undefined, {
      interview_id: interviewId,
      error_message: errorMessage,
    });
  },

  trackMediaPermissionDenied(interviewId, mediaType) {
    this.trackEvent('DeviceSetup', 'media_permission_denied', undefined, {
      interview_id: interviewId,
      media_type: mediaType,
    });
  },

  trackAttemptToAddQuestion(interviewId, problemId) {
    this.trackEvent('ProblemInteraction', 'attempt_to_add_question', undefined, {
      interview_id: interviewId,
      problem_id: problemId,
    });
  },

  trackQuestionAddedSuccessfully(interviewId, problemId, tags) {
    this.trackEvent('ProblemInteraction', 'question_added_successfully', undefined, {
      interview_id: interviewId,
      problem_id: problemId,
      associated_tags: tags,
    });
  },

  trackCodeExecuted(interviewId, problemId, language) {
    this.trackEvent('CodeEditor', 'code_executed', undefined, {
      interview_id: interviewId,
      problem_id: problemId,
      language,
      timestamp: Date.now(),
    });
  },

  trackInterviewOutcomeSubmitted(interviewId, outcome) {
    this.trackEvent('Feedback', 'interview_outcome_submitted', undefined, {
      interview_id: interviewId,
      outcome,
    });
  },
};

export default analytics;
